import { apiRequest, ApiError } from './apiClient';
import { API_BASE_URL } from '../config/api';

export const getTimekeepingStatus = () => apiRequest('/api/timekeeping/status');

export const clockIn = (locationId) => apiRequest('/api/timekeeping/clock-in', {
  method: 'POST',
  body: JSON.stringify(locationId ? { locationId: Number(locationId) } : {})
});

export const clockOut = () => apiRequest('/api/timekeeping/clock-out', {
  method: 'POST',
  body: JSON.stringify({})
});

const intervalAction = (kind, action) => apiRequest(`/api/timekeeping/${kind}/${action}`, { method: 'POST', body: JSON.stringify({}) });
export const startBreak = () => intervalAction('breaks', 'start');
export const endBreak = () => intervalAction('breaks', 'end');
export const startLunch = () => intervalAction('lunch', 'start');
export const endLunch = () => intervalAction('lunch', 'end');

export const getRecentTimeEntries = () => apiRequest('/api/timekeeping/entries?limit=10&offset=0');

export const getManagedTimeEntries = (filters = {}) => {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== '' && value != null) query.set(key, String(value));
  });
  return apiRequest(`/api/timekeeping/manage/entries?${query.toString()}`);
};

const queryFor = filters => {
  const query = new URLSearchParams();
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== '' && value != null) query.set(key, String(value));
  });
  return query.toString();
};

export const getTimekeepingExceptions = (filters = {}) => apiRequest(`/api/timekeeping/manage/exceptions?${queryFor(filters)}`);
export const getTimekeepingEmployeeOptions = (filters = {}) => apiRequest(`/api/timekeeping/manage/employee-options?${queryFor(filters)}`);
export const getApprovalWeek = filters => apiRequest(`/api/timekeeping/manage/approval-week?${queryFor(filters)}`);
export const getApprovalEmployees = filters => apiRequest(`/api/timekeeping/manage/approval-employees?${queryFor(filters)}`);
export const getApprovalQueue = filters => apiRequest(`/api/timekeeping/manage/bulk-approval-queue?${queryFor(filters)}`);
const approvalMutation = (action, payload, requestId) => apiRequest(`/api/timekeeping/manage/approval-week/${action}`, {
  method: 'POST', headers: { 'x-request-id': requestId }, body: JSON.stringify(payload)
});
export const approveTimecardWeek = (payload, requestId) => approvalMutation('approve', payload, requestId);
export const reopenTimecardWeek = (payload, requestId) => approvalMutation('reopen', payload, requestId);
export const bulkApproveTimecardWeeks = (items, requestId) => apiRequest('/api/timekeeping/manage/approval-weeks/bulk-approve', {
  method:'POST',headers:{'x-request-id':requestId},body:JSON.stringify({items})
});
export const createMissingTimeEntry = payload => apiRequest('/api/timekeeping/manage/entries/missing', {
  method: 'POST', body: JSON.stringify(payload)
});

export const getWorkedHoursReport = (filters = {}) => {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value !== '' && value != null) query.set(key, String(value)); });
  return apiRequest(`/api/timekeeping/reports/manage?${query.toString()}`);
};

export const getWorkedHoursEntries = (filters = {}) => {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value !== '' && value != null) query.set(key, String(value)); });
  return apiRequest(`/api/timekeeping/reports/manage/entries?${query.toString()}`);
};

const FILENAME_FROM_DISPOSITION = /filename="?([^";]+)"?/i;

export const downloadWorkedHoursCsv = async (filters = {}) => {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value !== '' && value != null) query.set(key, String(value)); });
  const response = await fetch(`${API_BASE_URL}/api/timekeeping/reports/manage/export?${query.toString()}`, {
    credentials: 'include'
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new ApiError(payload.error || `Export failed with status ${response.status}`, response.status, payload);
  }
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(FILENAME_FROM_DISPOSITION);
  const filename = match ? match[1] : 'timekeeping-worked-hours.csv';
  const blob = await response.blob();
  return { blob, filename };
};

export const getApprovedTimeExportPreview = filters => apiRequest(`/api/timekeeping/reports/manage/approved-export/preview?${queryFor(filters)}`);

export const downloadApprovedTimeCsv = async filters => {
  const response = await fetch(`${API_BASE_URL}/api/timekeeping/reports/manage/approved-export?${queryFor(filters)}`, { credentials: 'include' });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new ApiError(payload.error || `Export failed with status ${response.status}`, response.status, payload);
  }
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(FILENAME_FROM_DISPOSITION);
  return { blob: await response.blob(), filename: match ? match[1] : 'approved-recorded-time.csv' };
};
export const getApprovedTimeExportHistory = filters => apiRequest(`/api/timekeeping/reports/manage/approved-export-history?${queryFor(filters)}`);
export const getApprovedTimeExportBatch = batchId => apiRequest(`/api/timekeeping/reports/manage/approved-export-history/${encodeURIComponent(batchId)}`);
export const redownloadApprovedTimeExport = async batchId => {
  const response=await fetch(`${API_BASE_URL}/api/timekeeping/reports/manage/approved-export-history/${encodeURIComponent(batchId)}/artifact`,{credentials:'include'});
  if(!response.ok){const payload=await response.json().catch(()=>({}));throw new ApiError(payload.error||`Export retrieval failed with status ${response.status}`,response.status,payload);}
  const match=(response.headers.get('Content-Disposition')||'').match(FILENAME_FROM_DISPOSITION);
  return{blob:await response.blob(),filename:match?match[1]:'approved-recorded-time.csv'};
};

export const correctTimeEntry = (entryId, payload) => apiRequest(`/api/timekeeping/manage/entries/${entryId}`, {
  method: 'PATCH',
  body: JSON.stringify(payload)
});
export const correctTimeInterval = (intervalId, payload) => apiRequest(`/api/timekeeping/manage/intervals/${intervalId}`, { method: 'PATCH', body: JSON.stringify(payload) });

export const getManagedAttendance = (filters = {}) => {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value !== '' && value != null) query.set(key, String(value)); });
  return apiRequest(`/api/attendance/manage?${query.toString()}`);
};

export const getShiftAssociationCandidates = entryId => apiRequest(`/api/timekeeping/manage/entries/${entryId}/shift-association/candidates`);
export const resolveShiftAssociation = (entryId, payload) => apiRequest(`/api/timekeeping/manage/entries/${entryId}/shift-association`, { method: 'PATCH', body: JSON.stringify(payload) });
